(() => {
  const CART_SELECTOR =
    'cart-items[data-ct-gift-bundles], cart-drawer-items[data-ct-gift-bundles]';
  const ROLE_SELECTOR = '[data-ct-bundle-role]';

  const cartEndpoint = () => {
    if (window.routes?.cart_update_url) return window.routes.cart_update_url;

    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/update.js`;
  };

  const getBundleParts = (element) => {
    const scope = element.closest(CART_SELECTOR);
    const roleElement = element.closest(ROLE_SELECTOR);
    const bundleId = roleElement?.dataset.ctBundleId;
    const bundleItems = scope
      ? Array.from(scope.querySelectorAll(ROLE_SELECTOR)).filter(
          (item) => item.dataset.ctBundleId === bundleId
        )
      : [];
    const parent = bundleItems.find((item) => item.dataset.ctBundleRole === 'parent') || null;
    const addon = bundleItems.find((item) => item.dataset.ctBundleRole === 'addon') || null;

    return { scope, roleElement, parent, addon };
  };

  const updateCartLines = async (updates, loadingElement) => {
    loadingElement?.classList.add('is-updating');

    const isDrawerUpdate = Boolean(loadingElement?.closest('cart-drawer-items'));
    const requestBody = { updates };

    if (isDrawerUpdate) {
      requestBody.sections = ['cart-drawer', 'cart-icon-bubble'];
      requestBody.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(cartEndpoint(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to update this gift bundle.');
      }

      window.CTCartData?.invalidate();

      const cartDrawer = document.querySelector('cart-drawer');

      if (isDrawerUpdate && cartDrawer?.renderContents && data.sections) {
        cartDrawer.classList.toggle('is-empty', data.item_count === 0);
        cartDrawer.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (error) {
      loadingElement?.classList.remove('is-updating');
      console.error(error);
      window.alert(error.message || 'Unable to update this gift bundle. Please try again.');
    }
  };

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target.closest('input[name="updates[]"]');
      if (!input?.closest(CART_SELECTOR)) return;

      const { roleElement, parent, addon } = getBundleParts(input);
      if (!roleElement || !parent?.dataset.ctBundleId) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      const updates = {};

      if (roleElement.dataset.ctBundleRole === 'addon') {
        updates[roleElement.dataset.ctLineKey] = quantity;
      } else {
        updates[parent.dataset.ctLineKey] = quantity;

        if (addon?.dataset.ctLineKey) {
          updates[addon.dataset.ctLineKey] = quantity;
        }
      }

      updateCartLines(updates, parent);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const removeButton = event.target.closest('cart-remove-button');
      if (!removeButton?.closest(CART_SELECTOR)) return;

      const { roleElement, parent, addon } = getBundleParts(removeButton);
      if (!roleElement?.dataset.ctBundleId) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const updates = {};

      if (roleElement.dataset.ctBundleRole === 'addon') {
        updates[roleElement.dataset.ctLineKey] = 0;
      } else {
        if (!parent?.dataset.ctLineKey) return;
        updates[parent.dataset.ctLineKey] = 0;

        if (addon?.dataset.ctLineKey) {
          updates[addon.dataset.ctLineKey] = 0;
        }
      }

      updateCartLines(updates, parent);
    },
    true
  );

  let orphanCleanupPending = null;

  const cleanupOrphanGiftAddons = (includeDrawer = false) => {
    const mainCart = document.querySelector('cart-items[data-ct-gift-bundles]');
    const drawerItems = document.querySelector('cart-drawer-items[data-ct-gift-bundles]');

    if (!mainCart && !(includeDrawer && drawerItems)) return Promise.resolve();
    if (orphanCleanupPending) return orphanCleanupPending;

    orphanCleanupPending = (async () => {
      try {
        const response = await fetch(`${window.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
        const cart = await response.json();

        if (!response.ok || !Array.isArray(cart.items)) return;

        const parentBundleIds = new Set(
          cart.items
            .filter((item) => item.properties?._ct_addon_type !== 'gift_box')
            .map((item) => item.properties?._ct_bundle_id)
            .filter(Boolean)
        );
        const orphanAddons = cart.items.filter((item) => {
          if (item.properties?._ct_addon_type !== 'gift_box') return false;

          const bundleId = item.properties?._ct_bundle_id;
          return !bundleId || !parentBundleIds.has(bundleId);
        });

        if (!orphanAddons.length) return;

        const updates = Object.fromEntries(orphanAddons.map((item) => [item.key, 0]));
        await updateCartLines(updates, mainCart ? null : drawerItems);
      } catch (error) {
        console.error('Unable to clean up orphaned gift add-ons.', error);
      }
    })().finally(() => {
      orphanCleanupPending = null;
    });

    return orphanCleanupPending;
  };

  const clearCart = async (button) => {
    const confirmation = button.dataset.ctClearCartConfirm || 'Remove all items from your cart?';
    if (!window.confirm(confirmation)) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to clear the cart.');
      }

      window.CTCartData?.invalidate();
      window.location.reload();
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      console.error(error);
      window.alert(error.message || 'Unable to clear the cart. Please try again.');
    }
  };

  document.addEventListener('click', (event) => {
    const clearButton = event.target.closest('[data-ct-clear-cart]');
    if (clearButton) {
      clearCart(clearButton);
      return;
    }

    if (event.target.closest('#cart-icon-bubble')) {
      cleanupOrphanGiftAddons(true);
    }
  });

  cleanupOrphanGiftAddons();
})();
